/**
 * These helpers are built to add support to Google login on the client using
 * auth2 and storing the token using a GraphQL mutation.
 *
 * We also calculate the continueUrl depending on different factors:
 *  - It's the first time the user is logged in.
 *  - We use classCodes to join a certain classroom.
 *  - Is the user a teacher.
 *  - It's joining from districts. ...etc.
 *
 * @see https://khanacademy.atlassian.net/wiki/spaces/ENG/pages/650773166/Sign+in+with+Google
 */
import {t} from "@lingui/core/macro";
import gql from "graphql-tag";

import {
    joinClassroomsPostlogin,
    roleToUserRole,
    setAnalyticsCookies,
    type LoginTypeData,
    type LoginUserData,
    type Role,
} from "@repo/auth-shared";
import type {ApolloGqlClient} from "@repo/data-gql-apollo";
import {gqlOp, type GqlOperation} from "@repo/data-gql-core";
import {doNotTranslateYet} from "@repo/i18n";
import {Errors, KAError} from "@repo/logging";
import {redirect} from "@repo/urls";

import type {loginWithGoogleMutationType} from "./__graphql-types__/index.ts";

/**
 * Given an error code, return a user friendly error message.
 *
 * @param {string} errorCode The google login error code.
 * @returns {string} The user friendly message.
 */
export const getErrorMessageByCode = (errorCode: string): string => {
    switch (errorCode) {
        // Google auth error codes
        case "popup_blocked_by_browser":
            return t`Your browser blocked a popup window required for logging in. Please click on the new Continue with Google button and try logging in again.`;
        case "idpiframe_initialization_failed":
            return t`Your browser blocked third-party cookies required for logging in. Please change your browser settings to allow cookies on khanacademy.org.`;
        case "popup_closed_by_user":
            return t`You closed the popup window before logging in. If that was a mistake, try logging in again.`;
        // loginWithGoogle mutation
        case "ALREADY_LOGGED_IN":
            return t`You are currently logged in to another Google account. Please log out of that account and try again.`;
        // linkCurrentUserToUDIByUUID mutation
        // see: districts-link.js
        case "Failed to link the current user to an UDI":
            return doNotTranslateYet`You have been logged in / signed up with Google, but there's a problem linking this account to your class.`;
        case "INVITE_NOT_FOUND":
            return t`Your invitation was not found. Please try clicking the link in your email again.`;
        case "INVALID_TOKEN":
        default:
            return t`Oops! Something went wrong. Please try again.`;
    }
};

export const GOOGLE_LOGIN_MUTATION: GqlOperation<
    loginWithGoogleMutationType["response"],
    loginWithGoogleMutationType["variables"]
> = gqlOp(gql`
    mutation loginWithGoogleMutation(
        $token: String!
        $birthdate: String
        $linkClever: Boolean
        $role: UserRole
        $cleverTransferToken: String
        $inviteId: String
    ) {
        loginWithGoogle(
            token: $token
            birthdate: $birthdate
            linkClever: $linkClever
            role: $role
            cleverTransferToken: $cleverTransferToken
            inviteId: $inviteId
        ) {
            user {
                id
                kaid
                canAccessDistrictsHomepage
                isTeacher
                hasUnresolvedInvitations
                preferredKaLocale {
                    id
                    kaLocale
                    status
                }
            }
            isFirstLogin
            error {
                code
            }
        }
    }
`);

interface Options {
    // The Google auth token.
    token: string;
    // An optional list of signupCodes passed in the UI. e.g /join/ABC123.
    signupCodes?: Array<string>;
    // The user's birthdate.
    birthdate?: string;
    linkClever?: boolean;
    cleverTransferToken?: string;
    // "learner" | "teacher" | "parent"
    role?: Role;
    // The inviteId used in /invitesignup.
    inviteId?: string;
    // Whether we want to allow the user to link a shadowed Google account
    // (A shadowed Google account is one that is not yet associated with a Khan
    // account, but has the same email address as the Khan account.)
    disallowShadowLink?: boolean;
}

/**
 * Stores the Google auth token into our backend via GraphQL and tries to join a
 * classroom.
 *
 * @param {string} token The Googlen auth token.
 * @param {Array<string>} signupCodes An optional list of signupCodes passed in
 * the UI. e.g /join/ABC123.
 */
export const loginWithGoogle = async (
    gqlClient: ApolloGqlClient,
    options: Options,
): Promise<LoginTypeData> => {
    const {role, signupCodes, ...mutationOptions} = options;

    const variables = {
        ...mutationOptions,
        role: roleToUserRole(role),
    } as const;

    try {
        const result = await gqlClient.mutate(GOOGLE_LOGIN_MUTATION, {
            variables,
        });

        const error = result.data?.loginWithGoogle?.error;
        if (error) {
            const {code} = error;

            // If the code is SHADOWS_EXISTING_ACCOUNT and shadow link is allowed
            if (
                code === "SHADOWS_EXISTING_ACCOUNT" &&
                !options.disallowShadowLink
            ) {
                redirect(
                    `/login/oauth/verify?oauth_type=google&google_access_token=${options.token}`,
                );
            }

            // We pass the error code directly, so that code is mapped to an
            // UI readable message.
            throw new KAError(code, Errors.Internal);
        }

        // TODO(somewhatabstract): Data could be null.
        // @ts-expect-error - TS2322 - Type 'LoginUserData | undefined' is not assignable to type 'LoginUserData'.
        const user: LoginUserData = result.data?.loginWithGoogle?.user;
        // @ts-expect-error - TS2322 - Type 'boolean | undefined' is not assignable to type 'boolean'.
        const isFirstLogin: boolean =
            result.data?.loginWithGoogle?.isFirstLogin;

        const userData: LoginTypeData = {
            user,
            isFirstLogin,
        };

        // Only call the joinClassrooms mutation if the user is logged in and is
        // passing signupCodes.
        if (signupCodes && signupCodes.length > 0) {
            userData.joinClassrooms = await joinClassroomsPostlogin(
                gqlClient,
                signupCodes,
            );
        }

        setAnalyticsCookies(isFirstLogin, user.kaid);

        return userData;
    } catch (error: any) {
        // We handle the exception thrown from within the mutation success
        // handler.
        if (error instanceof KAError) {
            throw error;
        }

        // Otherwise we throw a generic error.
        throw new KAError("Failed to login", Errors.Internal);
    }
};
